/**
 * When a photo challenge counts as expired -- shared by every surface that
 * shows one.
 *
 * Both of these lived inside CardChallengeModal.jsx, and the collection grid
 * kept its own copy of the predicate. #260 made the modal's copy skew-proof and
 * left the grid's untouched, so the grid was still running the pre-#260 text
 * (`Date.parse(challenge.expiresAt) <= Date.now()`, byte-for-byte the version at
 * commit 8511d6c) while the modal ran the corrected one: on a device 20 minutes
 * fast the tile read "Challenge expired" over a modal whose Submit was armed
 * (#292). They live here so both surfaces read one definition.
 */

/**
 * The challenge's deadline as an instant on THIS DEVICE's clock (#260).
 *
 * `expiresAt` is minted against the SERVER's clock
 * (CARD_VERIFICATION_CHALLENGE_TTL_MINUTES = 15,
 * server/verification/verificationController.js). Comparing it directly to
 * Date.now() compares two different clocks, so a device 20 minutes fast read
 * every freshly minted challenge as five minutes dead: the "Challenge expired"
 * panel rendered the instant the modal opened, and Start minted another one
 * that was equally dead on arrival. Since #16 lifted that test out of the idle
 * branch there is no longer any way through it, so the lockout is total. The
 * collection grid runs the same predicate for its per-tile label, so the same
 * skew mislabels every tile there too (#292) -- the blast radius is both
 * surfaces, not the modal alone.
 *
 * Two payload fields make the skew cancel:
 *   - `serverNow`, the server clock at serialisation time, so
 *     `expiresAt - serverNow` is a LIFETIME -- a difference of two readings of
 *     one clock, carrying no skew at all;
 *   - `receivedAtMs`, stamped on the response in verificationApi.js, so the
 *     countdown starts at a DEVICE-clock instant.
 * `receivedAtMs + lifetime` is then a device-clock deadline, and the offset in
 * `receivedAtMs` cancels the offset in the `Date.now()` it is compared against.
 *
 * Counting from RECEIPT is the load-bearing part. Sampling the server clock at
 * render time instead cannot tell clock skew from payload age (the approach fix
 * sprint 91 tried and both skeptics killed): a challenge record reaches its
 * consumer -- the modal, or a grid tile -- minutes after the list that carried
 * it was fetched, so the sample read
 * those minutes as skew and handed every pending challenge that much extra life
 * on a perfectly correct clock.
 *
 * Falls back to the raw `expiresAt` whenever the pair is not both present and
 * numeric -- an older server, or any challenge object that did not come through
 * the API layer. That fallback is the pre-#260 behaviour exactly, which is what
 * keeps #16 pinned rather than turned into a NaN comparison that is false.
 */
export const deviceDeadlineMs = (challenge) => {
  const expiresAtMs = challenge?.expiresAt ? Date.parse(challenge.expiresAt) : NaN;
  const receivedAtMs = challenge?.receivedAtMs;
  const lifetimeMs = expiresAtMs - Date.parse(challenge?.serverNow);
  if (Number.isFinite(lifetimeMs) && Number.isFinite(receivedAtMs)) {
    return receivedAtMs + lifetimeMs;
  }
  return expiresAtMs;
};

export const isExpiredChallenge = (challenge) =>
  challenge?.status === "expired" ||
  (challenge?.status === "pending" && challenge?.expiresAt && deviceDeadlineMs(challenge) <= Date.now());
